import { Tag, type SelectOption } from '@components';
import { Box, Collapse, IconButton, Typography, useTheme } from '@mui/material';
import { useField } from 'formik';
import { ArrowDown2, Trash } from 'iconsax-react';
import { type ReactElement, useEffect, useMemo, useState } from 'react';

import type { Address } from '@core/types/api/Address/Address';
import { type DistanceInfo, getPlantDistanceInfo } from '@core/utils/projectHelpers';
import type { NestedKeyOf } from '@core/utils/types';
import type { YupSchemaReferenceType } from '@core/utils/yupHelpers';
import { useGetPlantsOptionsWithDistances } from '@web/hooks/plantsDistance/useGetPlantsOptionsWithDistances';

import { ExpandMore } from '..';
import { LookupInput } from '../LookupInput/LookupInput';
import { SlabCard } from '../SlabCard/SlabCard';
import { SlabCardBody } from '../SlabCard/SlabCardBody';
import { SlabCardHeader } from '../SlabCard/SlabCardHeader';

type Props<FState extends Record<string, any>, FPath extends NestedKeyOf<FState>> = {
  index: number;
  otherSelectedPlantIds: string[];
  onMatchChange: (value: string | null) => void;
  children: ReactElement;
  arrayHelpers: {
    remove: (index: number) => void;
  };
  formState: FState;
  formikName: FPath;
  readOnly?: boolean;
  /** Disables only the plant dropdown (e.g. when the plant is auto-synced from another section). */
  plantDisabled?: boolean;
  canRemove?: boolean;
  projectAddress?: Partial<Address>;
};

export const PlantAccordionFormik = <
  FState extends Record<string, any>,
  FPath extends NestedKeyOf<FState>,
>({
  index,
  formState,
  formikName,
  otherSelectedPlantIds,
  children,
  arrayHelpers,
  onMatchChange,
  readOnly = false,
  plantDisabled,
  canRemove = true,
  projectAddress,
}: Props<FState, FPath>): ReactElement => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [{ value: plantField }] = useField<YupSchemaReferenceType>(formikName);
  const plantId = plantField.id;
  const plantLabel = plantField.option?.label ?? null;

  const hasPlantId = Boolean(plantId);

  useEffect(() => {
    setIsOpen(Boolean(plantId));
  }, [plantId]);

  const {
    options: allPlantOptionsList,
    distances,
    isLoading: isLoadingOptions,
    isPlantListLoaded,
  } = useGetPlantsOptionsWithDistances({ currentAddress: projectAddress });

  const isMissingFromOptions =
    Boolean(plantId) && !allPlantOptionsList.some((option) => option.value === plantId);
  // The options only contain non-archived plants, so once they load a missing plant is archived.
  const isArchived = isMissingFromOptions && isPlantListLoaded;

  const plantOptions = useMemo((): SelectOption<string>[] => {
    const base = allPlantOptionsList.filter(
      (option) => !otherSelectedPlantIds.includes(option.value),
    );
    // LookupInput reads the displayed name from the options.
    if (isMissingFromOptions && plantId) {
      const selectedOption: SelectOption<string> = {
        value: plantId,
        label: plantLabel ?? plantId,
        sublabels: isArchived ? ['Archived'] : [],
      };
      return [...base, selectedOption];
    }
    return base;
  }, [
    allPlantOptionsList,
    otherSelectedPlantIds,
    isMissingFromOptions,
    isArchived,
    plantId,
    plantLabel,
  ]);

  let plantDistanceInfo: DistanceInfo | null = null;
  if (plantId) {
    const distance = distances.get(plantId);
    if (distance) {
      plantDistanceInfo = getPlantDistanceInfo(distance);
    }
  }

  return (
    <SlabCard
      sx={{
        borderLeft: `3px solid ${theme.palette.SlabGreen[600]}`,
      }}
    >
      <SlabCardHeader
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
          backgroundColor: theme.palette.SlabGreen[200],
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ExpandMore expand={isOpen} disabled={!hasPlantId} onClick={() => setIsOpen(!isOpen)}>
              <ArrowDown2 />
            </ExpandMore>
            <Box
              sx={{
                backgroundColor: theme.palette.SlabGreen[600],
                width: '20px',
                height: '20px',
                borderRadius: '8px',
                marginRight: '8px',
                marginLeft: '8px',
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='h6' fontWeight='bold'>
                Location {index + 1}
              </Typography>
              {isArchived && <Tag variant='Tertiary' label='Archived' />}
            </Box>
            <Box sx={{ display: 'flex', width: '18rem', marginLeft: '2rem' }}>
              <LookupInput
                formState={formState}
                name={formikName}
                options={plantOptions}
                isLoading={isLoadingOptions}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.common.white,
                  },
                  '& .MuiFormHelperText-root:not(.Mui-error)': {
                    display: 'none',
                  },
                  '& .MuiFormHelperText-root.Mui-error': {
                    position: 'absolute',
                    top: '100%',
                    marginTop: 0.25,
                    lineHeight: 1.2,
                  },
                }}
                onMatchChange={onMatchChange}
                placeholder='Search or select location'
                disabled={readOnly || plantDisabled}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: 'auto' }}>
              {plantDistanceInfo?.durationText && plantDistanceInfo?.distanceText && (
                <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
                  <Typography
                    component='span'
                    sx={{ marginRight: '0.25rem', color: theme.palette.text.secondary }}
                  >
                    Distance:
                  </Typography>
                  <Typography component='span' color={theme.palette.text.primary}>
                    {plantDistanceInfo?.distanceText}
                  </Typography>
                  <Typography component='span' sx={{ marginRight: '0.5rem', marginLeft: '0.5rem' }}>
                    |
                  </Typography>
                  <Typography
                    component='span'
                    sx={{ marginRight: '0.25rem', color: theme.palette.text.secondary }}
                  >
                    Drive time:
                  </Typography>
                  <Typography component='span' color={theme.palette.text.primary}>
                    {plantDistanceInfo?.durationText}
                  </Typography>
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {readOnly ? null : (
                  <IconButton onClick={() => arrayHelpers.remove(index)} disabled={!canRemove}>
                    <Trash variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </SlabCardHeader>
      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        <SlabCardBody>{children}</SlabCardBody>
      </Collapse>
    </SlabCard>
  );
};
